import { useState } from "react";
import { Drop, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImageCaptionField } from "@/components/ImageCaptionField";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Fresh milk, and the doorstep round that brings it: your days, your bottles, pause any time.
export const MilkRound = ({ data, mine, busy, act, where }) => {
  const [milk, setMilk] = useState({ kind: "whole milk", container: "glass bottle", size: "1 pint", price: 0, note: "", image_id: "", image_caption: "" });
  const [sub, setSub] = useState({});

  const toggle = (id, day, current) => {
    const days = current.includes(day) ? current.filter((d) => d !== day) : [...current, day];
    setSub({ ...sub, [id]: { ...sub[id], days } });
  };

  return (
    <div className="space-y-4" data-testid="milk-section">
      <div>
        <p className="overline text-slate-500">
          <Drop size={13} className="mr-1 inline align-[-2px] text-[#002FA7]" /> Fresh milk
        </p>
        {(data.milk || []).length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No dairy on the board yet.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.milk.map((m) => {
              const picked = sub[m.id]?.days || ["Monday", "Wednesday", "Friday"];
              return (
                <div key={m.id} className="border border-border bg-white p-3" data-testid={`milk-${m.id}`}>
                  {m.image_url && (
                    <img src={`${BACKEND}${m.image_url}`} alt={m.kind} loading="lazy"
                      className="mb-2 h-28 w-full border border-border object-cover" />
                  )}
                  <p className="text-sm font-semibold text-[#0F172A]">{m.kind}</p>
                  <p className="text-xs text-slate-500">
                    {m.size} {m.container} · {m.stall_name} · {m.where}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                    {m.symbol}{Number(m.price || 0).toFixed(2)} a {m.container}
                  </p>
                  {m.owner_id !== data.me.id && (
                    <div className="mt-2 space-y-2">
                      <div className="flex flex-wrap gap-1">
                        {(data.days || []).map((d) => (
                          <button key={d} type="button" data-testid={`milk-day-${m.id}-${d}`}
                            onClick={() => toggle(m.id, d, picked)}
                            className={`tap-safe border px-1.5 py-1 text-[11px] transition-colors duration-150 ${
                              picked.includes(d)
                                ? "border-[#002FA7] bg-[#002FA7] text-white"
                                : "border-border bg-white text-slate-600 hover:border-[#002FA7]"}`}>
                            {String(d).slice(0, 3)}
                          </button>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-1 text-xs text-slate-600">
                          Bottles a drop
                          <Input data-testid={`milk-qty-${m.id}`} type="number" min={1} className="w-16"
                            value={sub[m.id]?.quantity ?? 2}
                            onChange={(e) => setSub({ ...sub, [m.id]: { ...sub[m.id], days: picked, quantity: e.target.value } })} />
                        </label>
                        <select data-testid={`milk-pay-${m.id}`} aria-label="Pay"
                          value={sub[m.id]?.pay || "online"}
                          onChange={(e) => setSub({ ...sub, [m.id]: { ...sub[m.id], days: picked, pay: e.target.value } })}
                          className="tap-safe border border-border bg-white px-2 py-1.5 text-xs">
                          <option value="online">Pay per drop by card</option>
                          <option value="milkman">Pay the milkman</option>
                        </select>
                      </div>
                      <Input data-testid={`milk-address-${m.id}`} placeholder="Doorstep address"
                        value={sub[m.id]?.address || ""}
                        onChange={(e) => setSub({ ...sub, [m.id]: { ...sub[m.id], days: picked, address: e.target.value } })} />
                      <Button size="sm" data-testid={`milk-start-${m.id}`} disabled={busy}
                        onClick={() => act(() => api.post("/farm/milk/rounds", {
                          milk_id: m.id, days: picked,
                          quantity: Number(sub[m.id]?.quantity) || 2,
                          address: sub[m.id]?.address || "",
                          lat: where?.lat, lng: where?.lng,
                          pay: sub[m.id]?.pay || "online", reminders: true,
                        }), "You are on the round")}>
                        Start the round
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(data.my_rounds || []).length > 0 && (
        <div data-testid="milk-my-rounds">
          <p className="overline text-slate-500">Your milk round</p>
          <div className="mt-2 space-y-2">
            {data.my_rounds.map((r) => (
              <div key={r.id} className="border border-border bg-white p-3 text-sm" data-testid={`round-${r.id}`}>
                <p className="font-semibold text-[#0F172A]">
                  {r.reference} · {r.quantity} × {r.size} {r.kind} · {(r.days || []).join(", ")} ·
                  {" "}{r.symbol}{Number(r.per_drop || 0).toFixed(2)} a drop · {r.status}
                </p>
                <p className="text-xs text-slate-500">
                  {r.stall_name}{r.miles != null ? ` · ${r.miles} miles away` : ""} ·
                  {" "}{r.pay === "online" ? "paying by card" : "paying the milkman"}
                  {r.reminders && (
                    <span className="ml-1">
                      <BellRinging size={11} className="mr-1 inline align-[-2px]" />reminder the night before
                    </span>
                  )}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.status === "running" ? (
                    <Button size="sm" variant="outline" data-testid={`round-pause-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/farm/milk/rounds/${r.id}/change`, { status: "paused" }), "Paused")}>
                      Pause it
                    </Button>
                  ) : r.status === "paused" ? (
                    <Button size="sm" variant="outline" data-testid={`round-resume-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/farm/milk/rounds/${r.id}/change`, { status: "running" }), "Back on")}>
                      Start it again
                    </Button>
                  ) : null}
                  {r.status !== "cancelled" && (
                    <Button size="sm" variant="outline" data-testid={`round-cancel-${r.id}`} disabled={busy}
                      onClick={() => act(() => api.post(`/farm/milk/rounds/${r.id}/change`, { status: "cancelled" }), "Stopped")}>
                      Stop the round
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(data.drops || []).length > 0 && (
        <div data-testid="milk-drops">
          <p className="overline text-slate-500">Drops</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.drops.map((d) => (
              <li key={d.id} data-testid={`drop-${d.id}`}>
                {d.day} · {d.quantity} × {d.size} {d.kind} · {d.symbol}{Number(d.total || 0).toFixed(2)} ·
                {" "}{d.status} · {d.payment_status}
                {d.seller_id === data.me.id && d.status === "out" && (
                  <Button className="ml-2" size="sm" variant="outline" data-testid={`drop-delivered-${d.id}`}
                    disabled={busy}
                    onClick={() => act(() => api.post(`/farm/milk/drops/${d.id}/delivered`), "Left on the step")}>
                    Delivered
                  </Button>
                )}
                {d.user_id === data.me.id && d.pay === "online" && d.payment_status === "unpaid" && (
                  <Button className="ml-2" size="sm" data-testid={`drop-pay-${d.id}`} disabled={busy}
                    onClick={async () => {
                      try {
                        const { data: p } = await api.post("/payments/farm/milk/checkout", {
                          drop_id: d.id, origin_url: window.location.origin });
                        if (p.url) window.location.href = p.url;
                      } catch (e) { oops(e); }
                    }}>
                    Pay for it
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {mine && (
        <div className="border border-border bg-white p-4" data-testid="milk-put-up">
          <p className="overline text-slate-500">Dairies — put your milk up</p>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <select data-testid="milk-kind" value={milk.kind} aria-label="Milk"
              onChange={(e) => setMilk({ ...milk, kind: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.milk_kinds || []).map((k) => <option key={k} value={k}>{String(k)}</option>)}
            </select>
            <select data-testid="milk-container" value={milk.container} aria-label="Container"
              onChange={(e) => setMilk({ ...milk, container: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {(data.containers || []).map((c) => <option key={c} value={c}>{String(c)}</option>)}
            </select>
            <Input data-testid="milk-size" placeholder="Size — '1 pint', '2 litre'"
              value={milk.size} onChange={(e) => setMilk({ ...milk, size: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Your price
              <Input data-testid="milk-price" type="number" min={0} step="0.01" value={milk.price}
                onChange={(e) => setMilk({ ...milk, price: e.target.value })} className="w-24" />
            </label>
            <Input data-testid="milk-note" placeholder="Note — 'milked this morning'"
              value={milk.note} onChange={(e) => setMilk({ ...milk, note: e.target.value })} />
          </div>
          <div className="mt-3">
            <ImageCaptionField testid="milk-image" label="Photo of the milk — goes on the public board"
              value={milk.image_id} caption={milk.image_caption}
              onChange={({ image_id, image_caption }) => setMilk({ ...milk, image_id: image_id || "", image_caption })} />
          </div>
          <Button className="mt-2" size="sm" data-testid="milk-save" disabled={busy}
            onClick={() => act(async () => {
              await api.post("/farm/milk", {
                stall_id: mine.id, kind: milk.kind, container: milk.container,
                size: milk.size, price: Number(milk.price) || 0, note: milk.note,
                image_id: milk.image_id || "" });
              setMilk({ ...milk, note: "", image_id: "", image_caption: "" });
            }, "On the board")}>
            Put the milk up
          </Button>
        </div>
      )}

      {(data.round_customers || []).length > 0 && (
        <div data-testid="milk-customers">
          <p className="overline text-slate-500">Your round</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-700">
            {data.round_customers.map((r) => (
              <li key={r.id} data-testid={`customer-${r.id}`}>
                {r.name} · {r.address} · {r.quantity} × {r.size} {r.kind} ·
                {" "}{(r.days || []).join(", ")} · {r.status}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
